import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, Bot, User, Download, RefreshCw, CheckCircle2, MapPin,
  Star, Zap, X, BookmarkCheck, Mail, MessageCircle, Settings,
  Copy, Check, Sparkles, Play, Eye, Plus, List, Target,
  Phone, ChevronDown, ChevronUp, BarChart2
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const QUICK = [
  { e:'🏭', t:'Factory in China',       p:'Find factories in China' },
  { e:'📱', t:'Mobile manufacturers',   p:'Find mobile phone manufacturers' },
  { e:'🍽️', t:'Restaurants Mumbai',    p:'Find top restaurants in Mumbai with 4+ rating' },
  { e:'💻', t:'IT companies Bangalore', p:'Find IT companies in Bangalore' },
  { e:'🏥', t:'Hospitals Delhi',        p:'Find hospitals in Delhi' },
  { e:'🧵', t:'Textile suppliers',      p:'Find textile suppliers' },
  { e:'📧', t:'Draft campaign email',   p:'Draft a cold outreach email template for restaurant leads' },
  { e:'📊', t:'Analyze pipeline',       p:'Analyze my saved leads and give insights' },
];

function Bold({ text }) {
  if (!text) return null;
  return <>{text.split(/(\*\*.*?\*\*)/g).map((p,i)=>i%2===1?<strong key={i}>{p.replace(/\*\*/g,'')}</strong>:p)}</>;
}

function Dots() {
  return (
    <div style={{ display:'flex',gap:10,alignItems:'center' }}>
      <div style={{ width:32,height:32,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,var(--accent))',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
        <Bot size={16} style={{ color:'#fff' }}/>
      </div>
      <div style={{ background:'var(--bg3)',borderRadius:'4px 12px 12px 12px',padding:'12px 16px',display:'flex',gap:5 }}>
        <style>{`@keyframes dot{0%,80%,100%{opacity:.2}40%{opacity:1}}`}</style>
        {[0,1,2].map(i=><div key={i} style={{ width:8,height:8,borderRadius:'50%',background:'var(--text3)',animation:`dot 1.4s ${i*0.2}s infinite` }}/>)}
      </div>
    </div>
  );
}

function LCard({ lead, sel, onToggle }) {
  return (
    <div onClick={()=>!lead.already_saved&&onToggle(lead.id)}
      style={{ background:'var(--surface)',border:`2px solid ${sel?'var(--accent)':lead.already_saved?'var(--green)':'var(--border)'}`,borderRadius:10,padding:'10px 12px',cursor:lead.already_saved?'default':'pointer',transition:'border .15s',position:'relative' }}>
      {(sel||lead.already_saved)&&(
        <div style={{ position:'absolute',top:6,right:6,width:18,height:18,borderRadius:'50%',background:lead.already_saved?'var(--green)':'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center' }}>
          {lead.already_saved?<BookmarkCheck size={10} style={{color:'#fff'}}/>:<Check size={10} style={{color:'#fff'}}/>}
        </div>
      )}
      <div style={{ fontWeight:700,fontSize:13,marginBottom:3,paddingRight:22 }}>{lead.name}</div>
      {lead.address&&<div style={{ fontSize:11.5,color:'var(--text3)',display:'flex',alignItems:'center',gap:3,marginBottom:4 }}><MapPin size={9}/>{lead.address.slice(0,45)}{lead.address.length>45?'…':''}</div>}
      <div style={{ display:'flex',gap:8,fontSize:11,color:'var(--text3)',flexWrap:'wrap',marginBottom:4 }}>
        {lead.rating&&<span style={{ color:'var(--gold)',fontWeight:600 }}>★ {Number(lead.rating).toFixed(1)}</span>}
        {lead.business_type&&<span style={{ background:'var(--bg3)',padding:'1px 5px',borderRadius:3,textTransform:'capitalize' }}>{lead.business_type.replace(/_/g,' ')}</span>}
        {lead.lead_score>0&&<span style={{ color:lead.lead_score>=70?'var(--green)':lead.lead_score>=40?'var(--gold)':'var(--red)',fontWeight:700 }}>{lead.lead_score}pts</span>}
      </div>
      <div style={{ display:'flex',gap:8,fontSize:11 }}>
        {lead.email&&<span style={{ color:'var(--accent)',display:'flex',alignItems:'center',gap:3 }}><Mail size={9}/>{lead.email.slice(0,22)}{lead.email.length>22?'…':''}</span>}
        {lead.phone&&<span style={{ color:'#25d366',display:'flex',alignItems:'center',gap:3 }}><Phone size={9}/>{lead.phone}</span>}
      </div>
      {lead.already_saved&&<div style={{ fontSize:11,color:'var(--green)',fontWeight:600,marginTop:3 }}>✓ Saved</div>}
    </div>
  );
}

function DraftPanel({ draft, onClose, onUseDraft }) {
  const [copied,setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(draft); setCopied(true); setTimeout(()=>setCopied(false),2000); };
  const subjMatch = draft.match(/^(SUBJECT|Subject):\s*(.+)/m);
  const subject   = subjMatch ? subjMatch[2] : '';
  const body      = draft.replace(/^(SUBJECT|Subject):.+\n+/,'').trim();
  return (
    <div style={{ background:'var(--surface)',border:'1px solid var(--accent)',borderRadius:12,padding:16,marginTop:8 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10 }}>
        <div style={{ display:'flex',alignItems:'center',gap:6,fontWeight:700,fontSize:13,color:'var(--accent)' }}><Mail size={14}/>Email Draft</div>
        <div style={{ display:'flex',gap:8 }}>
          <button type="button" onClick={copy} style={{ display:'flex',alignItems:'center',gap:4,fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg3)',cursor:'pointer' }}>
            {copied?<Check size={11}/>:<Copy size={11}/>}{copied?'Copied':'Copy'}
          </button>
          {onUseDraft&&<button type="button" onClick={()=>onUseDraft(subject,body)} style={{ display:'flex',alignItems:'center',gap:4,fontSize:12,padding:'4px 10px',borderRadius:6,border:'none',background:'var(--accent)',color:'#fff',cursor:'pointer' }}>
            Use in Campaign
          </button>}
          <button type="button" onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',color:'var(--text3)' }}><X size={14}/></button>
        </div>
      </div>
      <pre style={{ fontSize:13,color:'var(--text2)',whiteSpace:'pre-wrap',lineHeight:1.6,margin:0,fontFamily:'inherit' }}>{draft}</pre>
    </div>
  );
}

// ── Bulk Send Modal ───────────────────────────────────────────────────────
function BulkSendModal({ leads, defaultChannel, onClose }) {
  const [channel, setChannel] = useState(defaultChannel||'email');
  const [subject, setSubject] = useState('');
  const [body,    setBody]    = useState('');
  const [genBusy, setGenB]    = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [result,  setResult]  = useState(null);

  // Count from lead data (may be null if not fetched from details API)
  const withEmail = leads.filter(l=>l.email).length;
  const withPhone = leads.filter(l=>l.phone).length;
  // If no contacts on lead objects, show total and let backend handle it
  const emailLabel = withEmail > 0 ? `${withEmail} have email` : `${leads.length} leads`;
  const phoneLabel = withPhone > 0 ? `${withPhone} have phone` : `${leads.length} leads`;

  const genAI = async () => {
    setGenB(true);
    try {
      const r = await api.post('/agent/generate-content', {
        channel, purpose: subject||'cold outreach', industry:'business', tone:'professional'
      });
      const txt = r.data.content || '';
      if (channel === 'email') {
        const sm = txt.match(/^(SUBJECT|Subject):\s*(.+)/m);
        if (sm) setSubject(sm[2].trim());
        setBody(txt.replace(/^(SUBJECT|Subject):.+\n+/,'').trim());
      } else {
        setBody(txt);
      }
      toast.success('AI content generated!');
    } catch { toast.error('AI generation failed'); }
    finally { setGenB(false); }
  };

  const send = async () => {
    if (!body.trim()) { toast.error('Write a message first'); return; }
    if (channel==='email' && !subject.trim()) { toast.error('Enter subject'); return; }
    setBusy(true);
    try {
      const ids = leads.map(l=>l.id);
      if (channel === 'email') {
        const r = await api.post('/agent/bulk-email',{ leadIds:ids, subject, body });
        setResult(r.data);
        toast.success(r.data.message);
      } else {
        const r = await api.post('/agent/bulk-whatsapp',{ leadIds:ids, message:body });
        setResult(r.data);
        toast.success(r.data.message);
      }
    } catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div className="modal-title">📤 Bulk Send to {leads.length} Leads</div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>
        <div className="modal-body" style={{ display:'flex',flexDirection:'column',gap:14 }}>
          <div style={{ display:'flex',gap:8 }}>
            {[['email',`📧 Email (${emailLabel})`],['whatsapp',`💬 WhatsApp (${phoneLabel})`]].map(([v,l])=>(
              <button key={v} type="button" onClick={()=>setChannel(v)}
                style={{ flex:1,padding:9,borderRadius:8,border:`2px solid ${channel===v?'var(--accent)':'var(--border)'}`,background:channel===v?'var(--accent-bg)':'transparent',cursor:'pointer',fontWeight:600,fontSize:12.5 }}>{l}</button>
            ))}
          </div>
          <div style={{ background:'var(--bg3)',borderRadius:8,padding:'8px 12px',fontSize:12.5,color:'var(--text3)' }}>
            ℹ️ {channel==='email'
              ? 'Emails will be sent to leads that have an email address on file.'
              : 'Messages will be sent to leads that have a phone number on file.'}
          </div>
          {channel==='email'&&(
            <div className="form-group">
              <label className="form-label">Subject *</label>
              <input className="input" value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Your Subject Line"/>
            </div>
          )}
          <div className="form-group">
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6 }}>
              <label className="form-label" style={{ margin:0 }}>Message * <span style={{ fontSize:11,color:'var(--text3)',fontWeight:400 }}>Use {'{business_name}'} as placeholder</span></label>
              <button type="button" onClick={genAI} disabled={genBusy} style={{ fontSize:12,padding:'3px 10px',borderRadius:6,border:'1px solid var(--accent)',background:'var(--accent-bg)',color:'var(--accent)',cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}>
                <Sparkles size={11}/>{genBusy?'Generating…':'AI Write'}
              </button>
            </div>
            <textarea className="textarea" value={body} onChange={e=>setBody(e.target.value)}
              placeholder={channel==='email'?'Dear {business_name},\n\nYour message...':'Hi {business_name}! 👋\n\nYour message...'}
              style={{ minHeight:130 }}/>
          </div>
          {result&&(
            <div style={{ background:'var(--green-bg)',border:'1px solid var(--green)',borderRadius:8,padding:'10px 14px',fontSize:13 }}>
              ✅ {result.message}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" onClick={send} disabled={busy||!!result}>
            {busy?<><div className="spinner spinner-sm"/>Sending…</>:channel==='email'?<><Mail size={13}/>Send Emails</>:<><MessageCircle size={13}/>Send WhatsApp</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Follow-up single lead modal ────────────────────────────────────────────
function FollowUpModal({ leads, onClose }) {
  const [ch,setCh]    = useState('email');
  const [to,setTo]    = useState('');
  const [sub,setSub]  = useState('Following up on your business');
  const [msg,setMsg]  = useState('');
  const [lid,setLid]  = useState('');
  const [ai,setAi]    = useState(true);
  const [busy,setBusy]= useState(false);
  const [sent,setSent]= useState(false);

  // Auto-fill contact when lead is selected
  useEffect(()=>{
    if(!lid) return;
    const lead = leads?.find(l=>l.id===lid);
    if(!lead) return;
    if(ch==='email'&&lead.email) setTo(lead.email);
    if(ch==='whatsapp'&&lead.phone) setTo(lead.phone);
  },[lid,ch]);

  const send = async () => {
    if(!to){ toast.error('Enter recipient'); return; }
    setBusy(true);
    try {
      const r = await api.post('/agent/followup',{ leadId:lid||null,channel:ch,recipient:to,subject:sub,message:msg,useAI:ai&&!msg });
      setMsg(r.data.message||'');
      setSent(true);
      toast.success('Sent!');
    } catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div className="modal-title">📤 Send Follow-up</div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>
        <div className="modal-body" style={{ display:'flex',flexDirection:'column',gap:14 }}>
          <div style={{ display:'flex',gap:8 }}>
            {[['email','📧 Email'],['whatsapp','💬 WhatsApp']].map(([v,l])=>(
              <button key={v} type="button" onClick={()=>{ setCh(v); setTo(''); }}
                style={{ flex:1,padding:8,borderRadius:8,border:`2px solid ${ch===v?'var(--accent)':'var(--border)'}`,background:ch===v?'var(--accent-bg)':'transparent',cursor:'pointer',fontWeight:600,fontSize:13 }}>{l}</button>
            ))}
          </div>
          {leads?.length>0&&(
            <div className="form-group">
              <label className="form-label">Lead (optional — auto-fills contact)</label>
              <select className="select" value={lid} onChange={e=>setLid(e.target.value)}>
                <option value="">— General follow-up —</option>
                {leads.map(l=><option key={l.id} value={l.id}>{l.name}{l.email?` · ${l.email}`:''}{l.phone?` · ${l.phone}`:''}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{ch==='email'?'Email Address *':'WhatsApp Number *'}</label>
            <input className="input" value={to} onChange={e=>setTo(e.target.value)} placeholder={ch==='email'?'contact@company.com':'+91 98765 43210'}/>
          </div>
          {ch==='email'&&<div className="form-group"><label className="form-label">Subject</label><input className="input" value={sub} onChange={e=>setSub(e.target.value)}/></div>}
          <div className="form-group">
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6 }}>
              <label className="form-label" style={{ margin:0 }}>Message</label>
              <label style={{ display:'flex',alignItems:'center',gap:5,fontSize:12,cursor:'pointer' }}>
                <input type="checkbox" checked={ai} onChange={e=>setAi(e.target.checked)}/><Sparkles size={11} style={{ color:'var(--accent)' }}/> AI-write
              </label>
            </div>
            <textarea className="textarea" value={msg} onChange={e=>setMsg(e.target.value)}
              placeholder={ai&&!msg?'Leave blank — AI writes a personalized message':'Your message...'}
              style={{ minHeight:90 }}/>
          </div>
          {sent&&<div style={{ background:'var(--green-bg)',border:'1px solid var(--green)',borderRadius:8,padding:'10px 14px',fontSize:13,color:'var(--green)' }}>✅ Sent!</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" onClick={send} disabled={busy}>
            {busy?<><div className="spinner spinner-sm"/>Sending…</>:ch==='email'?<><Mail size={13}/>Send Email</>:<><MessageCircle size={13}/>Send WhatsApp</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Message bubble ─────────────────────────────────────────────────────────
function Bubble({ msg, onSuggest, onFollowUp, onBulkSend, onSaveAll, onUseDraft, setTab }) {
  const [sel,setSel]       = useState([]);
  const [saving,setSaving] = useState(false);
  const [showDraft,setShowD]= useState(true);
  const toggle = id => setSel(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const unsaved = msg.leads?.filter(l=>!l.already_saved)||[];

  const saveSelected = async ids => {
    setSaving(true);
    try {
      const r = await api.post('/agent/bulk-save',{ leadIds:ids, listName:'AI Agent' });
      toast.success(r.data.message);
      ids.forEach(id=>{ const l=msg.leads?.find(x=>x.id===id); if(l)l.already_saved=true; });
      setSel([]);
      onSaveAll?.();
    } catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setSaving(false); }
  };

  const exportCSV = () => {
    if(!msg.leads?.length) return;
    const cols=['name','address','phone','email','rating','review_count','business_type','lead_score'];
    const csv=[cols.join(','),...msg.leads.map(l=>cols.map(c=>`"${(l[c]||'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
    const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='leads.csv'; a.click();
    toast.success('Exported!');
  };

  return (
    <div style={{ display:'flex',gap:10,alignItems:'flex-start',flexDirection:msg.role==='user'?'row-reverse':'row' }}>
      <div style={{ width:34,height:34,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',
        background:msg.role==='user'?'var(--accent)':'linear-gradient(135deg,#7c3aed,var(--accent))' }}>
        {msg.role==='user'?<User size={16} style={{color:'#fff'}}/>:<Bot size={16} style={{color:'#fff'}}/>}
      </div>
      <div style={{ maxWidth:'85%',display:'flex',flexDirection:'column',gap:8,minWidth:0 }}>
        <div style={{ background:msg.role==='user'?'var(--accent)':'var(--bg3)', color:msg.role==='user'?'#fff':'var(--text)',
          borderRadius:msg.role==='user'?'12px 4px 12px 12px':'4px 12px 12px 12px',
          padding:'10px 14px',fontSize:13.5,lineHeight:1.65,whiteSpace:'pre-wrap' }}>
          <Bold text={msg.content}/>
        </div>

        {/* Location suggestions */}
        {msg.suggest?.suggestions?.length>0&&(
          <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
            <div style={{ width:'100%',fontSize:12,color:'var(--text3)',fontWeight:600 }}>📍 Best locations for {msg.suggest.keyword}:</div>
            {msg.suggest.suggestions.map((loc,i)=>(
              <button key={i} type="button" onClick={()=>onSuggest(loc,msg.suggest.keyword)}
                style={{ padding:'6px 14px',borderRadius:20,border:'2px solid var(--accent)',background:'var(--accent-bg)',color:'var(--accent)',cursor:'pointer',fontWeight:600,fontSize:12.5,display:'flex',alignItems:'center',gap:5 }}>
                <MapPin size={11}/>{loc}
              </button>
            ))}
            {msg.suggest.reason&&<div style={{ width:'100%',fontSize:11.5,color:'var(--text3)',fontStyle:'italic' }}>{msg.suggest.reason}</div>}
          </div>
        )}

        {/* Email draft */}
        {msg.draft&&showDraft&&<DraftPanel draft={msg.draft} onClose={()=>setShowD(false)} onUseDraft={onUseDraft}/>}
        {msg.draft&&!showDraft&&(
          <button type="button" onClick={()=>setShowD(true)} style={{ display:'flex',alignItems:'center',gap:5,fontSize:12.5,padding:'5px 12px',borderRadius:8,border:'1px solid var(--accent)',background:'var(--accent-bg)',color:'var(--accent)',cursor:'pointer',width:'fit-content' }}>
            <Mail size={12}/>Show draft
          </button>
        )}

        {/* Leads */}
        {msg.leads?.length>0&&(
          <div>
            {/* Action toolbar */}
            <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:8,flexWrap:'wrap',padding:'8px 10px',background:'var(--bg3)',borderRadius:9 }}>
              <span style={{ fontSize:13,fontWeight:700 }}>{msg.leads.length} leads found</span>
              <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginLeft:'auto' }}>
                <button type="button" onClick={()=>setSel(sel.length===unsaved.length&&unsaved.length>0?[]:unsaved.map(l=>l.id))}
                  style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer' }}>
                  {sel.length===unsaved.length&&unsaved.length>0?'Deselect':'Select All'}
                </button>
                {sel.length>0&&(
                  <button type="button" onClick={()=>saveSelected(sel)} disabled={saving}
                    style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'none',background:'var(--accent)',color:'#fff',cursor:'pointer',fontWeight:600 }}>
                    {saving?'…':`💾 Save (${sel.length})`}
                  </button>
                )}
                {unsaved.length>0&&(
                  <button type="button" onClick={()=>saveSelected(unsaved.map(l=>l.id))} disabled={saving}
                    style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'none',background:'#10b981',color:'#fff',cursor:'pointer',fontWeight:600 }}>
                    {saving?'…':'💾 Save All'}
                  </button>
                )}
                {/* Bulk email */}
                <button type="button" onClick={()=>onBulkSend(msg.leads,'email')}
                  style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid var(--accent)',background:'var(--accent-bg)',color:'var(--accent)',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontWeight:600 }}>
                  <Mail size={11}/>Email All
                </button>
                {/* Bulk WhatsApp */}
                <button type="button" onClick={()=>onBulkSend(msg.leads,'whatsapp')}
                  style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid #25d366',background:'#25d36615',color:'#25d366',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontWeight:600 }}>
                  <MessageCircle size={11}/>WA All
                </button>
                {/* Single follow-up */}
                <button type="button" onClick={()=>onFollowUp(msg.leads)}
                  style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid var(--border)',background:'transparent',cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}>
                  📤 Follow-up
                </button>
                {/* <button type="button" onClick={exportCSV}
                  style={{ fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid var(--border)',background:'transparent',cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}>
                  <Download size={11}/>CSV
                </button> */}
              </div>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))',gap:8 }}>
              {msg.leads.map(l=><LCard key={l.id} lead={l} sel={sel.includes(l.id)} onToggle={toggle}/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Search Panel ──────────────────────────────────────────────────────────
function SearchPanel({ onSearch, onClose }) {
  const [form,setForm] = useState({ keyword:'',location:'',radius:5000,minRating:0,count:20,wantEmail:false,wantPhone:false });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const go = () => {
    if (!form.keyword.trim()) { toast.error('Enter a business type'); return; }
    if (!form.location.trim()) { toast.error('Enter a location'); return; }
    let p = `Find ${form.count} ${form.keyword} in ${form.location}`;
    if (form.radius!==5000)  p += ` within ${form.radius/1000}km`;
    if (form.minRating>0)    p += ` with ${form.minRating}+ rating`;
    if (form.wantEmail&&form.wantPhone) p += ` with email and phone`;
    else if (form.wantEmail) p += ` with email`;
    else if (form.wantPhone) p += ` with phone`;
    onSearch(p); onClose();
  };
  return (
    <div style={{ background:'var(--surface)',border:'2px solid var(--accent)',borderRadius:14,padding:18,marginBottom:12 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14 }}>
        <div style={{ fontWeight:800,fontSize:14,display:'flex',alignItems:'center',gap:7 }}><Target size={15} style={{ color:'var(--accent)' }}/>Advanced Lead Search</div>
        <button type="button" onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',color:'var(--text3)' }}><X size={15}/></button>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Business Type *</label>
          <input className="input" value={form.keyword} onChange={e=>set('keyword',e.target.value)} placeholder="restaurant, factory, hotel..." onKeyDown={e=>e.key==='Enter'&&go()}/>
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Location *</label>
          <input className="input" value={form.location} onChange={e=>set('location',e.target.value)} placeholder="Mumbai, China, Dubai..." onKeyDown={e=>e.key==='Enter'&&go()}/>
        </div>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10 }}>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Radius</label>
          <select className="select" value={form.radius} onChange={e=>set('radius',parseInt(e.target.value))}>
            {[1000,2000,5000,10000,20000,50000].map(r=><option key={r} value={r}>{r/1000}km</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Min Rating</label>
          <select className="select" value={form.minRating} onChange={e=>set('minRating',parseFloat(e.target.value))}>
            <option value={0}>Any</option>
            {[3,3.5,4,4.5].map(r=><option key={r} value={r}>{r}+ ★</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Count</label>
          <select className="select" value={form.count} onChange={e=>set('count',parseInt(e.target.value))}>
            {[10,20,30,50,60].map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display:'flex',gap:10,alignItems:'center',marginBottom:14 }}>
        <span style={{ fontSize:13,color:'var(--text2)',fontWeight:600 }}>Need leads with:</span>
        {[['wantEmail','📧 Email','var(--accent)'],['wantPhone','📞 Phone','#25d366']].map(([k,l,c])=>(
          <label key={k} style={{ display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',padding:'5px 12px',borderRadius:7,border:`2px solid ${form[k]?c:'var(--border)'}`,background:form[k]?`${c}18`:'transparent' }}>
            <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} style={{ accentColor:c }}/>{l}
          </label>
        ))}
      </div>
      <button type="button" className="btn btn-primary" onClick={go}><Zap size={13}/>Search Leads</button>
    </div>
  );
}

// ── SMTP Config ───────────────────────────────────────────────────────────
function SMTPConfig() {
  const [cfg,setCfg]    = useState({ host:'smtp.gmail.com',port:587,userEmail:'',password:'',fromName:'',fromEmail:'',secure:false });
  const [saved,setSaved]= useState(null);
  const [busy,setBusy]  = useState(false);
  const [testing,setT]  = useState(false);
  useEffect(()=>{ api.get('/agent/smtp').then(r=>{ if(r.data.config){ setSaved(r.data.config); setCfg(p=>({...p,...r.data.config,password:''})); } }).catch(()=>{}); },[]);
  const save = async () => {
    if(!cfg.host||!cfg.userEmail||!cfg.password){ toast.error('Host, email and password required'); return; }
    setBusy(true);
    try { await api.post('/agent/smtp',cfg); toast.success('SMTP config saved!'); setSaved(cfg); }
    catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setBusy(false); }
  };
  const test = async () => {
    setT(true);
    try { const r=await api.post('/agent/smtp/test'); toast.success(r.data.message); }
    catch(e){ toast.error(e.response?.data?.message||'Test failed'); }
    finally{ setT(false); }
  };
  const F=({label,k,type='text',placeholder=''})=>(
    <div className="form-group"><label className="form-label">{label}</label>
      <input className="input" type={type} placeholder={placeholder} value={cfg[k]||''} onChange={e=>setCfg(p=>({...p,[k]:e.target.value}))}/></div>
  );
  return (
    <div style={{ maxWidth:560 }}>
      <h3 style={{ fontFamily:'var(--font-display)',fontSize:18,fontWeight:800,marginBottom:6 }}>📧 Email (SMTP) Setup</h3>
      <p style={{ color:'var(--text3)',fontSize:13.5,lineHeight:1.6,marginBottom:16 }}>Configure your email to send campaigns and follow-ups. <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Gmail App Password guide →</a></p>
      {saved?.verified===1&&<div style={{ background:'var(--green-bg)',border:'1px solid var(--green)',borderRadius:9,padding:'10px 14px',fontSize:13,color:'var(--green)',marginBottom:16,display:'flex',alignItems:'center',gap:8 }}><CheckCircle2 size={14}/> SMTP verified and active</div>}
      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        <div className="form-row"><F label="SMTP Host *" k="host" placeholder="smtp.gmail.com"/><F label="Port" k="port" placeholder="587"/></div>
        <F label="Email Address *" k="userEmail" type="email" placeholder="you@gmail.com"/>
        <F label="Password / App Password *" k="password" type="password" placeholder="Your password or app password"/>
        <div className="form-row"><F label="From Name" k="fromName" placeholder="Your Name or Company"/><F label="From Email" k="fromEmail" placeholder="Same or different email"/></div>
        <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13.5,cursor:'pointer' }}>
          <input type="checkbox" checked={!!cfg.secure} onChange={e=>setCfg(p=>({...p,secure:e.target.checked}))}/>Use SSL (port 465)
        </label>
        <div style={{ display:'flex',gap:10 }}>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy?<><div className="spinner spinner-sm"/>Saving…</>:'Save Configuration'}</button>
          {saved&&<button type="button" className="btn btn-ghost" onClick={test} disabled={testing}>{testing?<><div className="spinner spinner-sm"/>Testing…</>:<><Play size={13}/>Test Connection</>}</button>}
        </div>
      </div>
      <div style={{ marginTop:24,background:'var(--bg3)',borderRadius:10,padding:'14px 16px',fontSize:13,color:'var(--text2)',lineHeight:1.8 }}>
        <strong>Popular SMTP settings:</strong><br/>
        📨 <strong>Gmail:</strong> smtp.gmail.com, port 587 (Use App Password)<br/>
        📨 <strong>Outlook:</strong> smtp-mail.outlook.com, port 587<br/>
        📨 <strong>Yahoo:</strong> smtp.mail.yahoo.com, port 587<br/>
        📨 <strong>SendGrid:</strong> smtp.sendgrid.net, port 587
      </div>
    </div>
  );
}

// ── WhatsApp Config ───────────────────────────────────────────────────────
function WAConfig() {
  const [cfg,setCfg]    = useState({ phoneId:'',accessToken:'',businessName:'' });
  const [saved,setSaved]= useState(null);
  const [busy,setBusy]  = useState(false);
  const [testing,setT]  = useState(false);
  const [testNo,setTNo] = useState('');
  useEffect(()=>{ api.get('/agent/whatsapp').then(r=>{ if(r.data.config){ setSaved(r.data.config); setCfg(p=>({...p,...r.data.config})); } }).catch(()=>{}); },[]);
  const save = async () => {
    if(!cfg.phoneId||!cfg.accessToken){ toast.error('Phone ID and access token required'); return; }
    if(!/^\d{10,20}$/.test(cfg.phoneId.trim())){ toast.error('Phone Number ID must be numeric ID from Meta Console'); return; }
    setBusy(true);
    try { await api.post('/agent/whatsapp',cfg); toast.success('WhatsApp config saved!'); setSaved(cfg); }
    catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setBusy(false); }
  };
  const test = async () => {
    if(!testNo){ toast.error('Enter a test phone number'); return; }
    setT(true);
    try { const r=await api.post('/agent/whatsapp/test',{ testPhone:testNo }); toast.success(r.data.message); }
    catch(e){ toast.error(e.response?.data?.message||'Test failed'); }
    finally{ setT(false); }
  };
  return (
    <div style={{ maxWidth:560 }}>
      <h3 style={{ fontFamily:'var(--font-display)',fontSize:18,fontWeight:800,marginBottom:6 }}>💬 WhatsApp Business Setup</h3>
      <p style={{ color:'var(--text3)',fontSize:13.5,lineHeight:1.6,marginBottom:16 }}>Connect WhatsApp Business API for automated follow-ups. <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Get credentials →</a></p>
      {saved?.status==='active'&&<div style={{ background:'var(--green-bg)',border:'1px solid var(--green)',borderRadius:9,padding:'10px 14px',fontSize:13,color:'var(--green)',marginBottom:16,display:'flex',alignItems:'center',gap:8 }}><CheckCircle2 size={14}/> WhatsApp connected: {saved.business_name||saved.phone_id}</div>}
      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        <div className="form-group"><label className="form-label">Business Name</label><input className="input" value={cfg.businessName||''} onChange={e=>setCfg(p=>({...p,businessName:e.target.value}))} placeholder="Your Business Name"/></div>
        <div className="form-group">
          <label className="form-label">Phone Number ID * <span style={{ fontSize:11,color:'var(--text3)',fontWeight:400 }}>(numeric ID from Meta Console, NOT your phone)</span></label>
          <input className="input" value={cfg.phoneId||''} onChange={e=>setCfg(p=>({...p,phoneId:e.target.value}))} placeholder="e.g. 123456789012345"/>
        </div>
        <div className="form-group"><label className="form-label">Access Token *</label><input className="input" type="password" value={cfg.accessToken||''} onChange={e=>setCfg(p=>({...p,accessToken:e.target.value}))} placeholder="Your permanent access token"/></div>
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy?<><div className="spinner spinner-sm"/>Saving…</>:'Save Configuration'}</button>
        {saved&&<div style={{ display:'flex',gap:10,alignItems:'flex-end' }}>
          <div className="form-group" style={{ flex:1,margin:0 }}>
            <label className="form-label">Test Phone Number</label>
            <input className="input" value={testNo} onChange={e=>setTNo(e.target.value)} placeholder="+91 98765 43210"/>
          </div>
          <button type="button" className="btn btn-ghost" onClick={test} disabled={testing}>{testing?<><div className="spinner spinner-sm"/>Sending…</>:<><Play size={13}/>Test</>}</button>
        </div>}
      </div>
      <div style={{ marginTop:24,background:'var(--bg3)',borderRadius:10,padding:'14px 16px',fontSize:13,color:'var(--text2)',lineHeight:1.8 }}>
        <strong>How to get credentials:</strong><br/>
        1. <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>developers.facebook.com</a> → Create App → Business type<br/>
        2. Add WhatsApp product → Getting Started<br/>
        3. <strong>Phone Number ID</strong> = 15-digit number shown there (NOT your phone number)<br/>
        4. Generate permanent token in System Users<br/>
        <div style={{ background:'var(--red-bg)',border:'1px solid var(--red)',borderRadius:7,padding:'7px 10px',marginTop:10,color:'var(--red)',fontSize:12.5 }}>
          ❌ Do NOT enter your phone number as Phone Number ID
        </div>
      </div>
    </div>
  );
}

// ── Campaigns Tab ─────────────────────────────────────────────────────────
function CampaignsTab({ lists }) {
  const [camps,setCamps]    = useState([]);
  const [view,setView]      = useState('list');
  const [selId,setSelId]    = useState(null);
  const [logs,setLogs]      = useState([]);
  const [busy,setBusy]      = useState(false);
  const [running,setRunning]= useState(null);
  const [form,setForm]      = useState({ name:'',channel:'email',subject:'',body:'',fromName:'',fromEmail:'',replyTo:'',targetList:'Default',targetStatus:'',targetAudience:'saved' });
  const [genBusy,setGenB]   = useState(false);

  const load = useCallback(async()=>{ try{ const r=await api.get('/agent/campaigns'); setCamps(r.data.campaigns||[]); }catch{} },[]);
  useEffect(()=>{ load(); },[load]);

  const create = async () => {
    if(!form.name||!form.body){ toast.error('Name and message body required'); return; }
    if(form.channel==='email'&&!form.subject){ toast.error('Subject required for email campaigns'); return; }
    setBusy(true);
    try {
      await api.post('/agent/campaigns',form);
      toast.success('Campaign created!'); load();
      setView('list');
      setForm({ name:'',channel:'email',subject:'',body:'',fromName:'',fromEmail:'',replyTo:'',targetList:'Default',targetStatus:'',targetAudience:'saved' });
    } catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setBusy(false); }
  };

  const run = async id => {
    if(!window.confirm('Send this campaign now to all matching leads?')) return;
    setRunning(id);
    try { const r=await api.post(`/agent/campaigns/${id}/run`); toast.success(r.data.message); load(); }
    catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setRunning(null); }
  };

  const viewLogs = async id => {
    setSelId(id);
    try { const r=await api.get(`/agent/campaigns/${id}/logs`); setLogs(r.data.logs||[]); setView('logs'); }
    catch{ toast.error('Failed to load logs'); }
  };

  // AI Generate — uses campaign name for context
  const genAI = async () => {
    if(!form.name){ toast.error('Enter campaign name first'); return; }
    setGenB(true);
    try {
      const r = await api.post('/agent/generate-content', {
        channel: form.channel, purpose: form.name, industry: form.name, tone:'professional'
      });
      const txt = r.data.content || '';
      if (form.channel === 'email') {
        const sm = txt.match(/^(SUBJECT|Subject):\s*(.+)/m);
        if (sm) setForm(p=>({...p, subject:sm[2].trim()}));
        setForm(p=>({...p, body:txt.replace(/^(SUBJECT|Subject):.+\n+/,'').trim()}));
      } else {
        setForm(p=>({...p, body:txt}));
      }
      toast.success('AI content generated from campaign name!');
    } catch{ toast.error('AI generation failed'); }
    finally{ setGenB(false); }
  };

  const STATUS_COLOR = { draft:'var(--text3)',scheduled:'var(--gold)',running:'var(--blue)',completed:'var(--green)',paused:'var(--red)' };

  if(view==='logs') return (
    <div>
      <button type="button" onClick={()=>setView('list')} style={{ display:'flex',alignItems:'center',gap:6,background:'none',border:'none',cursor:'pointer',color:'var(--accent)',fontSize:13,marginBottom:16,padding:0 }}>← Back</button>
      <h3 style={{ fontFamily:'var(--font-display)',fontSize:16,fontWeight:800,marginBottom:14 }}>Campaign Logs ({logs.length})</h3>
      {logs.length===0?<div style={{ textAlign:'center',color:'var(--text3)',padding:'32px 0' }}>No logs yet</div>:(
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Ch</th><th>Lead</th><th>Contact</th><th>Status</th><th>Sent</th></tr></thead>
            <tbody>{logs.map(l=>(
              <tr key={l.id}>
                <td style={{ fontSize:16 }}>{l.channel==='whatsapp'?'💬':'📧'}</td>
                <td style={{ fontSize:13 }}>{l.lead_name||'—'}</td>
                <td style={{ fontSize:12,color:'var(--text3)' }}>{l.email||l.phone||'—'}</td>
                <td><span className={`badge ${l.status==='sent'?'badge-green':l.status==='failed'?'badge-red':'badge-blue'}`}>{l.status}</span></td>
                <td style={{ fontSize:12,color:'var(--text3)' }}>{new Date(l.sent_at).toLocaleString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );

  if(view==='create') return (
    <div style={{ maxWidth:640 }}>
      <button type="button" onClick={()=>setView('list')} style={{ display:'flex',alignItems:'center',gap:6,background:'none',border:'none',cursor:'pointer',color:'var(--accent)',fontSize:13,marginBottom:16,padding:0 }}>← Back</button>
      <h3 style={{ fontFamily:'var(--font-display)',fontSize:18,fontWeight:800,marginBottom:20 }}>Create Campaign</h3>
      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        {/* Channel */}
        <div className="form-group">
          <label className="form-label">Channel *</label>
          <div style={{ display:'flex',gap:8 }}>
            {[['email','📧 Email'],['whatsapp','💬 WhatsApp']].map(([v,l])=>(
              <button key={v} type="button" onClick={()=>setForm(p=>({...p,channel:v}))}
                style={{ flex:1,padding:9,borderRadius:8,border:`2px solid ${form.channel===v?'var(--accent)':'var(--border)'}`,background:form.channel===v?'var(--accent-bg)':'transparent',cursor:'pointer',fontWeight:700,fontSize:13 }}>{l}</button>
            ))}
          </div>
        </div>
        {/* Name */}
        <div className="form-group">
          <label className="form-label">Campaign Name * <span style={{ fontSize:11,color:'var(--text3)',fontWeight:400 }}>(AI uses this to generate relevant content)</span></label>
          <input className="input" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Restaurant Outreach Q2, Hotel Partnership Mumbai"/>
        </div>
        {/* Target audience */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Target Audience *</label>
            <select className="select" value={form.targetAudience} onChange={e=>setForm(p=>({...p,targetAudience:e.target.value}))}>
              <option value="saved">My Saved Leads</option>
              <option value="assigned">Assigned to Me</option>
              <option value="team">My Team Leads</option>
              <option value="all">All Leads</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Target List</label>
            <select className="select" value={form.targetList} onChange={e=>setForm(p=>({...p,targetList:e.target.value}))}>
              <option value="Default">All Lists</option>
              {lists.map(l=><option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        {/* Email from fields */}
        {form.channel==='email'&&(
          <div className="form-row">
            <div className="form-group"><label className="form-label">From Name</label><input className="input" value={form.fromName} onChange={e=>setForm(p=>({...p,fromName:e.target.value}))} placeholder="Your Name"/></div>
            <div className="form-group"><label className="form-label">From Email</label><input className="input" type="email" value={form.fromEmail} onChange={e=>setForm(p=>({...p,fromEmail:e.target.value}))} placeholder="you@company.com"/></div>
          </div>
        )}
        {/* Subject (email only) */}
        {form.channel==='email'&&(
          <div className="form-group">
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6 }}>
              <label className="form-label" style={{ margin:0 }}>Subject *</label>
              <button type="button" onClick={genAI} disabled={genBusy} style={{ fontSize:12,padding:'3px 10px',borderRadius:6,border:'1px solid var(--accent)',background:'var(--accent-bg)',color:'var(--accent)',cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}>
                <Sparkles size={11}/>{genBusy?'Generating…':'AI Generate'}
              </button>
            </div>
            <input className="input" value={form.subject} onChange={e=>setForm(p=>({...p,subject:e.target.value}))} placeholder="Your Subject Line"/>
          </div>
        )}
        {/* Body */}
        <div className="form-group">
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6 }}>
            <label className="form-label" style={{ margin:0 }}>
              {form.channel==='whatsapp'?'WhatsApp Message *':'Email Body *'}
              {' '}<span style={{ fontSize:11,color:'var(--text3)',fontWeight:400 }}>Use {'{business_name}'}</span>
            </label>
            {form.channel==='whatsapp'&&(
              <button type="button" onClick={genAI} disabled={genBusy} style={{ fontSize:12,padding:'3px 10px',borderRadius:6,border:'1px solid var(--accent)',background:'var(--accent-bg)',color:'var(--accent)',cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}>
                <Sparkles size={11}/>{genBusy?'Generating…':'AI Generate'}
              </button>
            )}
          </div>
          <textarea className="textarea" value={form.body} onChange={e=>setForm(p=>({...p,body:e.target.value}))}
            placeholder={form.channel==='whatsapp'?'Hi {business_name}! 👋\n\nYour message...':'Dear {business_name},\n\nYour message...'}
            style={{ minHeight:form.channel==='whatsapp'?110:170 }}/>
        </div>
        {/* Target status */}
        <div className="form-group">
          <label className="form-label">Filter by Status (optional)</label>
          <select className="select" value={form.targetStatus} onChange={e=>setForm(p=>({...p,targetStatus:e.target.value}))}>
            <option value="">All statuses</option>
            {['new','contacted','qualified','converted','lost'].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>
        <div style={{ background:'var(--bg3)',borderRadius:9,padding:'10px 14px',fontSize:13,color:'var(--text3)' }}>
          {form.channel==='whatsapp'
            ? '💬 Sends to leads with phone numbers at 1 msg/sec. Requires WhatsApp setup.'
            : '📧 Sends to leads with email addresses. Requires SMTP setup in Email Setup tab.'}
        </div>
        <div style={{ display:'flex',gap:10 }}>
          <button type="button" className="btn btn-ghost" onClick={()=>setView('list')}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={create} disabled={busy}>
            {busy?<><div className="spinner spinner-sm"/>Creating…</>:`Create ${form.channel==='whatsapp'?'WhatsApp':'Email'} Campaign`}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
        <div><h3 style={{ fontFamily:'var(--font-display)',fontSize:18,fontWeight:800,marginBottom:4 }}>Campaigns</h3>
          <p style={{ color:'var(--text3)',fontSize:13 }}>Email and WhatsApp campaigns to your leads</p></div>
        <button type="button" className="btn btn-primary btn-sm" onClick={()=>setView('create')}><Plus size={13}/>New Campaign</button>
      </div>
      {camps.length===0?(
        <div className="empty-state">
          <div style={{ fontSize:48 }}>📧</div>
          <div style={{ fontWeight:700,fontSize:17 }}>No campaigns yet</div>
          <div style={{ color:'var(--text3)',fontSize:13.5 }}>Create email or WhatsApp campaigns to reach all your leads at once</div>
          <button type="button" className="btn btn-primary btn-sm" onClick={()=>setView('create')}><Plus size={13}/>Create Campaign</button>
        </div>
      ):(
        <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
          {camps.map(c=>(
            <div key={c.id} className="card" style={{ padding:'14px 16px' }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:12 }}>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:4 }}>
                    <span style={{ fontSize:16 }}>{c.channel==='whatsapp'?'💬':'📧'}</span>
                    <div style={{ fontWeight:700,fontSize:14 }}>{c.name}</div>
                    <span style={{ fontSize:11,padding:'2px 7px',borderRadius:10,background:'var(--bg3)',color:STATUS_COLOR[c.status]||'var(--text3)',fontWeight:600 }}>{c.status}</span>
                  </div>
                  {c.subject&&<div style={{ fontSize:12.5,color:'var(--text3)',marginBottom:5 }}>{c.subject}</div>}
                  <div style={{ display:'flex',gap:12,fontSize:12,color:'var(--text3)' }}>
                    <span>Audience: {c.target_audience||'saved'}</span>
                    <span>List: {c.target_list||'All'}</span>
                    {c.sent_count>0&&<span style={{ color:'var(--green)' }}>✅ {c.sent_count} sent</span>}
                    {c.fail_count>0&&<span style={{ color:'var(--red)' }}>❌ {c.fail_count} failed</span>}
                  </div>
                </div>
                <div style={{ display:'flex',gap:8,flexShrink:0 }}>
                  <button type="button" onClick={()=>viewLogs(c.id)} className="btn btn-ghost btn-sm"><Eye size={13}/>Logs</button>
                  {['draft','paused'].includes(c.status)&&(
                    <button type="button" onClick={()=>run(c.id)} disabled={running===c.id} className="btn btn-primary btn-sm">
                      {running===c.id?<><div className="spinner spinner-sm"/>Running…</>:<><Play size={13}/>Run</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────
function LogsTab() {
  const [logs,setLogs] = useState([]);
  const [loading,setL] = useState(true);
  useEffect(()=>{ api.get('/agent/logs').then(r=>setLogs(r.data.logs||[])).catch(()=>{}).finally(()=>setL(false)); },[]);
  if(loading) return <div className="empty-state"><div className="spinner spinner-lg"/></div>;
  return (
    <div>
      <h3 style={{ fontFamily:'var(--font-display)',fontSize:18,fontWeight:800,marginBottom:16 }}>Follow-up Activity Log</h3>
      {logs.length===0?<div className="empty-state"><div style={{ fontSize:40 }}>📋</div><div style={{ fontWeight:600 }}>No follow-ups sent yet</div></div>:(
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Ch</th><th>Lead / Recipient</th><th>Subject / Message</th><th>Status</th><th>Sent At</th></tr></thead>
            <tbody>{logs.map(l=>(
              <tr key={l.id}>
                <td style={{ fontSize:18 }}>{l.channel==='email'?'📧':'💬'}</td>
                <td style={{ fontSize:13 }}>{l.lead_name||l.recipient}</td>
                <td style={{ fontSize:12.5,color:'var(--text3)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{l.subject||l.message?.slice(0,50)||'—'}</td>
                <td><span className={`badge ${l.status==='sent'?'badge-green':'badge-red'}`}>{l.status}</span></td>
                <td style={{ fontSize:12,color:'var(--text3)',whiteSpace:'nowrap' }}>{new Date(l.sent_at||l.created_at).toLocaleString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function AIAgentPage() {
  const [tab,setTab]       = useState('chat');
  const [msgs,setMsgs]     = useState([{
    id:1, role:'assistant',
    content:`👋 Hi! I'm your **AI Agent**.\n\nI find business leads **globally** and help you reach them via email and WhatsApp.\n\nTry:\n• **"Find factories in China"**\n• **"Mobile manufacturer"** — I'll suggest best cities\n• **"Find restaurants in Mumbai"**\n• **"Draft email for hotel leads"**\n• **"Analyze my pipeline"**`,
    leads:null, draft:null, suggest:null,
  }]);
  const [input,setInput]   = useState('');
  const [busy,setBusy]     = useState(false);
  const [context,setCxt]   = useState({});
  const [followUp,setFU]   = useState(null);
  const [bulkSend,setBulk] = useState(null); // { leads, channel }
  const [showSearch,setSS] = useState(false);
  const [lists,setLists]   = useState([]);
  // For "Use in Campaign" from draft
  const [draftForCampaign,setDFC] = useState(null);
  const bottom = useRef(null);
  const inputR = useRef(null);

  useEffect(()=>{
    api.get('/leads/saved',{params:{limit:500}}).then(r=>{
      const ls=[...new Set((r.data.leads||[]).map(l=>l.list_name).filter(Boolean))];
      setLists(ls);
    }).catch(()=>{});
  },[]);

  useEffect(()=>{ bottom.current?.scrollIntoView({ behavior:'smooth' }); },[msgs,busy]);

  const send = async text => {
    const msg=(text||input).trim();
    if(!msg||busy) return;
    setInput(''); setSS(false);
    setMsgs(p=>[...p,{ id:Date.now(),role:'user',content:msg,leads:null,draft:null,suggest:null }]);
    setBusy(true);
    try {
      const history=msgs.slice(-10).map(m=>({role:m.role,content:m.content}));
      const r=await api.post('/agent/chat',{ message:msg,history,context });
      setMsgs(p=>[...p,{ id:Date.now()+1,role:'assistant',content:r.data.reply,leads:r.data.leads||null,draft:r.data.draft||null,suggest:r.data.suggest||null }]);
      if(r.data.context) setCxt(c=>({...c,...r.data.context}));
      if(r.data.leads)   setCxt(c=>({...c,lastLeads:r.data.leads}));
    } catch(e){
      setMsgs(p=>[...p,{ id:Date.now()+1,role:'assistant',content:'⚠️ '+(e.response?.data?.message||'Something went wrong.'),leads:null,draft:null,suggest:null }]);
    } finally{ setBusy(false); inputR.current?.focus(); }
  };

  const handleUseDraft = (subject,body) => {
    setDFC({ subject,body });
    setTab('campaigns');
    toast.success('Draft ready — create a campaign to use it');
  };

  const TABS = [
    { id:'chat',     icon:<Zap size={15}/>,          label:'AI Chat'    },
    { id:'campaigns',icon:<Mail size={15}/>,          label:'Campaigns'  },
    { id:'logs',     icon:<List size={15}/>,          label:'Logs'       },
    { id:'smtp',     icon:<Settings size={15}/>,      label:'Email Setup'},
    { id:'whatsapp', icon:<MessageCircle size={15}/>, label:'WhatsApp'   },
  ];

  return (
    <div style={{ maxWidth:1200,margin:'0 auto',padding:'0 20px' }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',gap:12,padding:'16px 0 0' }}>
        <div style={{ width:40,height:40,borderRadius:11,background:'linear-gradient(135deg,#7c3aed,var(--accent))',display:'flex',alignItems:'center',justifyContent:'center' }}>
          <Zap size={20} style={{ color:'#fff' }}/>
        </div>
        <div>
          <div style={{ fontFamily:'var(--font-display)',fontSize:20,fontWeight:800 }}>AI Agent</div>
          <div style={{ fontSize:12,color:'var(--text3)' }}>Lead search · Email campaigns · WhatsApp · Analysis</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex',gap:2,borderBottom:'2px solid var(--border)',margin:'14px 0 0' }}>
        {TABS.map(t=>(
          <button key={t.id} type="button" onClick={()=>setTab(t.id)}
            style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 16px',borderRadius:'8px 8px 0 0',border:'none',cursor:'pointer',fontSize:13.5,fontWeight:tab===t.id?700:500,background:tab===t.id?'var(--surface)':'transparent',color:tab===t.id?'var(--accent)':'var(--text3)',borderBottom:tab===t.id?'2px solid var(--accent)':'2px solid transparent',marginBottom:-2 }}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* Chat tab */}
      {tab==='chat'&&(
        <div style={{ height:'calc(100vh - 200px)',display:'flex',flexDirection:'column' }}>
          {/* Search panel */}
          {showSearch&&<SearchPanel onSearch={txt=>send(txt)} onClose={()=>setSS(false)}/>}

          {/* Messages */}
          <div style={{ flex:1,overflowY:'auto',padding:'16px 0',display:'flex',flexDirection:'column',gap:14 }}>
            {msgs.map(m=>(
              <Bubble key={m.id} msg={m}
                onSuggest={(loc,kw)=>send(`Find ${kw} in ${loc}`)}
                onFollowUp={leads=>setFU(leads)}
                onBulkSend={(leads,channel)=>setBulk({ leads,channel })}
                onSaveAll={()=>{}}
                onUseDraft={handleUseDraft}
                setTab={setTab}/>
            ))}
            {busy&&<Dots/>}
            <div ref={bottom}/>
          </div>

          {/* Quick actions */}
          {msgs.length<=2&&!busy&&(
            <div style={{ padding:'8px 0',flexShrink:0 }}>
              <div style={{ fontSize:11.5,color:'var(--text3)',fontWeight:700,marginBottom:8,textTransform:'uppercase',letterSpacing:.8 }}>Quick Start</div>
              <div style={{ display:'flex',gap:7,flexWrap:'wrap' }}>
                {QUICK.map((q,i)=>(
                  <button key={i} type="button" onClick={()=>send(q.p)}
                    style={{ fontSize:12,padding:'5px 12px',borderRadius:16,border:'1px solid var(--border)',background:'var(--bg3)',cursor:'pointer',color:'var(--text2)',display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap' }}>
                    {q.e} {q.t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{ padding:'12px 0',borderTop:'1px solid var(--border)',flexShrink:0 }}>
            <div style={{ display:'flex',gap:8,marginBottom:8 }}>
              <button type="button" onClick={()=>setSS(p=>!p)}
                style={{ fontSize:12,padding:'5px 12px',borderRadius:8,border:`2px solid ${showSearch?'var(--accent)':'var(--border)'}`,background:showSearch?'var(--accent-bg)':'transparent',cursor:'pointer',color:showSearch?'var(--accent)':'var(--text3)',display:'flex',alignItems:'center',gap:5,fontWeight:600 }}>
                <Target size={12}/>Advanced Search
              </button>
              <button type="button" onClick={()=>setTab('campaigns')}
                style={{ fontSize:12,padding:'5px 12px',borderRadius:8,border:'1px solid var(--border)',background:'transparent',cursor:'pointer',color:'var(--text3)',display:'flex',alignItems:'center',gap:5 }}>
                <Mail size={12}/>Campaign
              </button>
              <button type="button" onClick={()=>{ setMsgs([{ id:Date.now(),role:'assistant',content:'Chat cleared! What do you need?',leads:null,draft:null,suggest:null }]); setCxt({}); setSS(false); }}
                style={{ fontSize:12,padding:'5px 12px',borderRadius:8,border:'1px solid var(--border)',background:'transparent',cursor:'pointer',color:'var(--text3)',display:'flex',alignItems:'center',gap:4 }}>
                <RefreshCw size={12}/>Clear
              </button>
            </div>
            <div style={{ display:'flex',gap:10,alignItems:'flex-end' }}>
              <textarea ref={inputR} value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send();} }}
                placeholder='Ask anything... "Find factories in China", "Draft email for restaurant leads", "Analyze my pipeline"'
                rows={2} disabled={busy}
                style={{ flex:1,padding:'11px 14px',borderRadius:12,border:'1px solid var(--border)',background:'var(--bg3)',color:'var(--text)',fontSize:13.5,resize:'none',fontFamily:'inherit',outline:'none',lineHeight:1.5 }}/>
              <button type="button" onClick={()=>send()} disabled={!input.trim()||busy}
                style={{ width:44,height:44,borderRadius:12,border:'none',background:input.trim()&&!busy?'var(--accent)':'var(--bg3)',color:input.trim()&&!busy?'#fff':'var(--text3)',cursor:input.trim()&&!busy?'pointer':'not-allowed',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'background .15s' }}>
                <Send size={17}/>
              </button>
            </div>
            <div style={{ fontSize:11,color:'var(--text3)',marginTop:5,textAlign:'center' }}>
              Enter to send · Shift+Enter for new line · AI Powered
            </div>
          </div>
        </div>
      )}

      {/* Other tabs */}
      {tab!=='chat'&&(
        <div style={{ padding:'24px 0' }}>
          {tab==='campaigns'&&<CampaignsTab lists={lists} draftForCampaign={draftForCampaign} onDraftUsed={()=>setDFC(null)}/>}
          {tab==='logs'&&<LogsTab/>}
          {tab==='smtp'&&<SMTPConfig/>}
          {tab==='whatsapp'&&<WAConfig/>}
        </div>
      )}

      {/* Modals */}
      {followUp&&<FollowUpModal leads={followUp} onClose={()=>setFU(null)}/>}
      {bulkSend&&<BulkSendModal leads={bulkSend.leads} defaultChannel={bulkSend.channel} onClose={()=>setBulk(null)}/>}
    </div>
  );
}